import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Text, Box, Button, TextInput, Loader, Group, ActionIcon, Stack } from '@mantine/core';
import type { TreeApi } from 'react-arborist';
import { Link } from 'react-router-dom';
import { LocationHierarchyTree, countNodes } from '../location-hierarchy/LocationHierarchyTree';
import type { LocationTreeNode } from '../api/schedulingApis';
import { useLocationsTreeStore } from '../stores/locationsTreeStore';
import { IconX, IconSearch } from '@tabler/icons-react';
import { APP_ROUTES } from '../navigation/appRoutes';
import { revealLocationInTree } from '../location-hierarchy/locationTreeNavigation';
import {
  buildTypePillCounts,
  filterTreeByLocationType,
  findNodeById,
  formatLocationTypeLabel,
  hasRootNode,
} from '../features/schedule-locations/logic/locationManagementUtils';

export interface LocationHierarchyFloatingPanelProps {
  /** When true, the panel is visible. No overlay – you can interact with the page behind. */
  opened: boolean;
  onClose: () => void;
  projectId: string;
  /**
   * Task the panel is assigning a location for. When this changes while the panel stays open
   * (e.g. user clicks another Location cell), selection/scope seed resets for the new task.
   */
  taskId?: string | null;
  /** Called when user clicks Set on a location row. May return a promise. */
  onSelectLocation?: (location: LocationTreeNode) => void | Promise<void>;
  selectedLocationId?: string | null;
  /** Optional: location name for pre-selecting a node when id is not available */
  selectedLocationName?: string | null;
  /** Optional: when set, only these location IDs are selectable (e.g. from task context). Null = all enabled. */
  enabledLocationIds?: Set<string> | null;
  title?: string;
  /** Initial position when opened. Default: { x: 80, y: 80 } */
  defaultPosition?: { x: number; y: number };
  /** Initial size when opened. Default: { width: 520, height: 460 } */
  defaultSize?: { width: number; height: number };
  /** Opens reassign confirmation (parent). Header shows Reassign all when provided. */
  onReassignLocations?: () => void;
  reassignLocationsLoading?: boolean;
}

const PANEL_WIDTH = 520;
const PANEL_HEIGHT = 460;
const MIN_PANEL_WIDTH = 400;
const MIN_PANEL_HEIGHT = 320;
const HEADER_HEIGHT = 49;
const TOOLBAR_HEIGHT = 49;
const Z_INDEX = 1000;
const RESIZE_HANDLE_SIZE = 6;
/** Minimum top (px) so the header stays visible and the panel can always be dragged back. */
const MIN_PANEL_TOP = 8;

function clampPanelPosition(
  pos: { x: number; y: number },
  size: { width: number; height: number },
): { x: number; y: number } {
  const maxX = typeof window !== 'undefined' ? Math.max(0, window.innerWidth - size.width) : pos.x;
  const maxY =
    typeof window !== 'undefined'
      ? Math.max(MIN_PANEL_TOP, window.innerHeight - HEADER_HEIGHT)
      : pos.y;
  return {
    x: Math.max(0, Math.min(pos.x, maxX)),
    y: Math.max(MIN_PANEL_TOP, Math.min(pos.y, maxY)),
  };
}

/**
 * Draggable floating panel for Location Hierarchy (set-location only).
 * - No overlay: the rest of the page stays interactive.
 * - Drag by the header to move the panel.
 * - Close via the close button only (clicking outside does not close).
 */
export function LocationHierarchyFloatingPanel({
  opened,
  onClose,
  projectId,
  taskId = null,
  onSelectLocation,
  selectedLocationId = null,
  selectedLocationName = null,
  enabledLocationIds = null,
  title = 'Location Hierarchy',
  defaultPosition = { x: 80, y: 80 },
  defaultSize = { width: PANEL_WIDTH, height: PANEL_HEIGHT },
  onReassignLocations,
  reassignLocationsLoading = false,
}: LocationHierarchyFloatingPanelProps) {
  const [position, setPosition] = useState(defaultPosition);
  const [size, setSize] = useState(defaultSize);
  const [nodeCount, setNodeCount] = useState(0);
  const [treeData, setTreeData] = useState<LocationTreeNode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [treeExpanded, setTreeExpanded] = useState(true);
  const [assigningLocation, setAssigningLocation] = useState(false);
  const locationTreeApiRef = useRef<TreeApi<LocationTreeNode> | null>(null);

  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [isDragging, setIsDragging] = useState(false);

  type ResizeDirection = 'e' | 's' | 'se';
  const resizeRef = useRef({
    active: false,
    direction: 'e' as ResizeDirection,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
  });
  const [isResizing, setIsResizing] = useState(false);
  const prevOpenedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const didRevealOnOpenRef = useRef(false);
  /** Location id we should scroll to once on open — not updated by later user clicks. */
  const revealTargetIdRef = useRef<string | null>(null);
  /** After the user clicks a row, never re-seed from the task's open-time location. */
  const userPickedRef = useRef(false);
  const didSeedSelectionRef = useRef(false);

  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      setPosition(clampPanelPosition(defaultPosition, defaultSize));
      setSize(defaultSize);
      prevOpenedRef.current = true;
    }
    if (!opened) {
      prevOpenedRef.current = false;
      didRevealOnOpenRef.current = false;
      revealTargetIdRef.current = null;
      userPickedRef.current = false;
      didSeedSelectionRef.current = false;
    }
  }, [opened, defaultPosition.x, defaultPosition.y, defaultSize.width, defaultSize.height]);

  // Reset highlight/seed when the panel opens OR the target task changes while it stays open.
  // Do not depend on selectedLocationId — that also changes after Set on the same task.
  useEffect(() => {
    if (!opened) return;
    userPickedRef.current = false;
    didRevealOnOpenRef.current = false;
    didSeedSelectionRef.current = false;
    const initial =
      selectedLocationId != null && String(selectedLocationId).trim() !== ''
        ? String(selectedLocationId)
        : null;
    setSelectedId(initial);
    revealTargetIdRef.current = initial;
    setSearchTerm('');
    setTypeFilter(null);
    setTreeExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: task switch / open only
  }, [opened, taskId]);

  const fetchTreeIfNeeded = useLocationsTreeStore((s) => s.fetchTreeIfNeeded);

  useEffect(() => {
    if (!opened || !projectId) return;
    setLoaded(false);
    fetchTreeIfNeeded(projectId)
      .then((data) => {
        setTreeData(data);
        setNodeCount(countNodes(data));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [opened, projectId, fetchTreeIfNeeded]);

  // Seed highlight from task location once when tree loads — never after the user has clicked.
  useEffect(() => {
    if (!opened || treeData.length === 0) return;
    if (userPickedRef.current || didSeedSelectionRef.current) return;

    let resolved: string | null = null;
    if (selectedLocationId) {
      const node = findNodeById(treeData, selectedLocationId);
      if (node) resolved = String(node.location_id);
    }
    if (!resolved && selectedLocationName) {
      const stack = [...treeData];
      while (stack.length) {
        const n = stack.shift()!;
        if (n.name === selectedLocationName) {
          resolved = String(n.location_id);
          break;
        }
        if (n.child?.length) stack.push(...n.child);
      }
    }
    didSeedSelectionRef.current = true;
    if (!resolved) return;
    revealTargetIdRef.current = resolved;
    setSelectedId(resolved);
  }, [opened, treeData, selectedLocationId, selectedLocationName]);

  // Scroll to the task's location once on open / task switch. Do not re-bind selection afterward.
  useEffect(() => {
    if (!opened || !loaded || didRevealOnOpenRef.current) return;
    const targetId = revealTargetIdRef.current;
    if (!targetId || !findNodeById(treeData, targetId)) return;
    const treeSnapshot = treeData;
    let cancelled = false;
    let attempts = 0;

    const tryReveal = async () => {
      if (cancelled || userPickedRef.current) return;
      const api = locationTreeApiRef.current;
      if (!api) {
        if (attempts < 40) {
          attempts += 1;
          window.setTimeout(() => {
            void tryReveal();
          }, 50);
        }
        return;
      }
      const ok = await revealLocationInTree(api, treeSnapshot, targetId);
      if (cancelled || userPickedRef.current) return;
      if (ok) {
        didRevealOnOpenRef.current = true;
        return;
      }
      if (attempts < 40) {
        attempts += 1;
        window.setTimeout(() => {
          void tryReveal();
        }, 50);
      }
    };

    const timer = window.setTimeout(() => {
      void tryReveal();
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, loaded, treeData.length, taskId]);

  // Focus search after reveal has had a chance to run.
  useEffect(() => {
    if (!opened || !loaded) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 400);
    return () => window.clearTimeout(timer);
  }, [opened, loaded]);

  const handleExpandAll = useCallback(() => {
    locationTreeApiRef.current?.openAll();
    setTreeExpanded(true);
  }, []);

  const handleCollapseAll = useCallback(() => {
    locationTreeApiRef.current?.closeAll();
    setSearchTerm('');
    setTreeExpanded(false);
  }, []);

  const handleToggleExpandCollapse = useCallback(() => {
    if (treeExpanded) handleCollapseAll();
    else handleExpandAll();
  }, [treeExpanded, handleCollapseAll, handleExpandAll]);

  const onResizeStart = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        active: true,
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: size.width,
        startHeight: size.height,
      };
      setIsResizing(true);
    },
    [size.width, size.height],
  );

  useEffect(() => {
    if (!opened) return;
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current.active) return;
      const { direction, startX, startY, startWidth, startHeight } = resizeRef.current;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      setSize(() => {
        let w = startWidth;
        let h = startHeight;
        if (direction === 'e' || direction === 'se') {
          w = Math.max(MIN_PANEL_WIDTH, startWidth + dx);
        }
        if (direction === 's' || direction === 'se') {
          h = Math.max(MIN_PANEL_HEIGHT, startHeight + dy);
        }
        return { width: w, height: h };
      });
    };
    const onUp = () => {
      resizeRef.current.active = false;
      setIsResizing(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [opened]);

  const handleSelectLocation = useCallback(
    async (location: LocationTreeNode) => {
      if (!onSelectLocation || assigningLocation) return;
      const id = location.location_id != null ? String(location.location_id) : null;
      if (id) {
        userPickedRef.current = true;
        didSeedSelectionRef.current = true;
        setSelectedId(id);
      }
      setAssigningLocation(true);
      try {
        await Promise.resolve(onSelectLocation(location));
      } finally {
        setAssigningLocation(false);
        window.setTimeout(() => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }, 0);
      }
    },
    [onSelectLocation, assigningLocation],
  );

  const handleDataLoad = useCallback((data: LocationTreeNode[], count: number) => {
    setNodeCount(count);
    setTreeData(data);
  }, []);

  const handleTreeSelectionChange = useCallback((nodes: LocationTreeNode[]) => {
    if (nodes.length === 0) return;
    const id = nodes[0]?.location_id != null ? String(nodes[0].location_id) : null;
    if (!id) return;
    userPickedRef.current = true;
    didSeedSelectionRef.current = true;
    setSelectedId(id);
  }, []);

  const onHeaderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('button, input, a')) return;
      e.preventDefault();
      setIsDragging(true);
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startLeft: position.x,
        startTop: position.y,
      };
    },
    [position.x, position.y],
  );

  useEffect(() => {
    if (!opened) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging) return;
      const next = clampPanelPosition(
        {
          x: dragRef.current.startLeft + (e.clientX - dragRef.current.startX),
          y: dragRef.current.startTop + (e.clientY - dragRef.current.startY),
        },
        size,
      );
      setPosition(next);
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [opened, size.width, size.height]);

  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
    } else if (isDragging) {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, isResizing]);

  const typePills = useMemo(() => buildTypePillCounts(treeData), [treeData]);
  const filteredTree = useMemo(
    () => filterTreeByLocationType(treeData, typeFilter),
    [treeData, typeFilter],
  );
  // Drive row highlight from React state (like Location Management). Do not use arborist's
  // controlled `selection` prop — it re-selects the open-time id and snaps clicks back.
  const highlightedLocationIds = useMemo(
    () => (selectedId ? new Set([selectedId]) : undefined),
    [selectedId],
  );
  const showEmptyHierarchy = loaded && (treeData.length === 0 || !hasRootNode(treeData));
  const showFilteredEmpty =
    loaded && !showEmptyHierarchy && typeFilter != null && filteredTree.length === 0;

  const treeHeight = Math.max(120, size.height - HEADER_HEIGHT - TOOLBAR_HEIGHT - 16);

  if (!opened) return null;

  const panel = (
    <div
      role="dialog"
      aria-label={title}
      className="location-hierarchy-picker-panel"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: Z_INDEX,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        className="location-hierarchy-picker-header"
        onMouseDown={onHeaderMouseDown}
        style={{
          cursor: isResizing ? 'default' : isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Text className="location-hierarchy-picker-title">{title}</Text>
        <Group
          gap={6}
          wrap="nowrap"
          style={{ flexShrink: 0 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Button
            size="compact-xs"
            variant="default"
            loading={reassignLocationsLoading}
            disabled={!onReassignLocations || reassignLocationsLoading}
            onClick={() => onReassignLocations?.()}
          >
            Reassign all
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Close"
            onClick={onClose}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </div>

      <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {!loaded ? (
          <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader size="sm" />
          </Box>
        ) : showEmptyHierarchy ? (
          <Stack gap="sm" p="md" style={{ flex: 1, justifyContent: 'center' }}>
            <Text size="sm" c="dimmed">
              No locations yet. Create and edit the hierarchy in Location Management, then return
              here to set a location on the task.
            </Text>
            <Button
              className="location-mgmt-ai-btn location-mgmt-ai-btn--primary"
              component={Link}
              to={APP_ROUTES.schedule.locations}
              onClick={onClose}
              style={{ alignSelf: 'flex-start' }}
            >
              Open Location Management
            </Button>
          </Stack>
        ) : (
          <>
            <Box className="location-mgmt-toolbar location-hierarchy-picker-toolbar">
              <div className="location-mgmt-toolbar-pills">
                <Group gap={8} wrap="nowrap" style={{ width: 'max-content' }}>
                  <Text className="location-mgmt-toolbar-count" component="span">
                    {nodeCount} Locations
                  </Text>
                  {typePills.map(({ type, count }) => (
                    <button
                      key={type}
                      type="button"
                      className="location-mgmt-type-pill"
                      data-active={typeFilter === type ? 'true' : 'false'}
                      onClick={() => setTypeFilter((prev) => (prev === type ? null : type))}
                    >
                      {formatLocationTypeLabel(type)}
                      <span className="location-mgmt-type-pill-sep" aria-hidden>
                        ·
                      </span>
                      <span className="location-mgmt-type-pill-count">{count}</span>
                    </button>
                  ))}
                </Group>
              </div>
              <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
                <TextInput
                  className="location-hierarchy-picker-search"
                  placeholder="Search locations..."
                  size="xs"
                  ref={searchInputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.currentTarget.value)}
                  leftSection={<IconSearch size={14} />}
                  rightSection={
                    searchTerm ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Clear search"
                        onClick={() => setSearchTerm('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : null
                  }
                />
                <Button
                  className="location-mgmt-toolbar-btn"
                  variant="default"
                  onClick={handleToggleExpandCollapse}
                >
                  {treeExpanded ? 'Collapse all' : 'Expand all'}
                </Button>
              </Group>
            </Box>

            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                padding: '8px 12px 12px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {showFilteredEmpty ? (
                <Stack gap={6} p="md">
                  <Text size="sm" c="dimmed">
                    No locations of type &quot;{formatLocationTypeLabel(typeFilter!)}&quot;.
                  </Text>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setTypeFilter(null)}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Clear filter
                  </Button>
                </Stack>
              ) : (
                <LocationHierarchyTree
                  projectId={projectId}
                  variant="picker"
                  onDataLoad={handleDataLoad}
                  onSelectionChange={handleTreeSelectionChange}
                  onQuickSelectLocation={onSelectLocation ? handleSelectLocation : undefined}
                  selectedLocationId={null}
                  highlightedLocationIds={highlightedLocationIds}
                  activeLocationId={selectedId}
                  searchTerm={searchTerm}
                  enabledLocationIds={enabledLocationIds}
                  treeApiRef={locationTreeApiRef}
                  dataOverride={filteredTree}
                  disableEdit
                  disableDrag
                  width="100%"
                  height={treeHeight}
                />
              )}
            </Box>
          </>
        )}
      </Box>

      <div
        role="none"
        onMouseDown={(e) => onResizeStart(e, 'e')}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: RESIZE_HANDLE_SIZE,
          width: RESIZE_HANDLE_SIZE,
          cursor: 'ew-resize',
        }}
      />
      <div
        role="none"
        onMouseDown={(e) => onResizeStart(e, 's')}
        style={{
          position: 'absolute',
          left: 0,
          right: RESIZE_HANDLE_SIZE,
          bottom: 0,
          height: RESIZE_HANDLE_SIZE,
          cursor: 'ns-resize',
        }}
      />
      <div
        role="none"
        onMouseDown={(e) => onResizeStart(e, 'se')}
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: RESIZE_HANDLE_SIZE * 2,
          height: RESIZE_HANDLE_SIZE * 2,
          cursor: 'nwse-resize',
        }}
      />
    </div>
  );

  return createPortal(panel, document.body);
}
